import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { formatDateReadable } from '../utils/dateUtils';
import { normalizeSession, getBatchesArray } from '../utils/sessionUtils';
import { 
  MdHistory, 
  MdCalendarToday, 
  MdSearch,
  MdCheckCircle,
  MdCancel,
  MdSchedule,
  MdPeople
} from 'react-icons/md';

/**
 * Attendance History Component
 * Displays past attendance records with date filters
 */
function AttendanceHistory() {
  const navigate = useNavigate();
  const { getToken } = useAttendanceAuth();
  
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [recoveryWindow, setRecoveryWindow] = useState(null);
  const [teachingWeeks, setTeachingWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');

  useEffect(() => {
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setToDate(today.toISOString().split('T')[0]);
    setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedWeek]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const result = await attendanceAPI.getHistory({ 
        from_date: fromDate, 
        to_date: toDate, 
        teaching_week: selectedWeek || undefined,
        limit: 100 
      }, token);
      
      if (result.success && result.sessions) {
        setAttendances(result.sessions);
        setRecoveryWindow(result.recovery_window || null);
        setTeachingWeeks(result.teaching_weeks || []);
      } else {
        setAttendances([]);
        setRecoveryWindow(null);
      }
    } catch (err) {
      console.error('Error fetching attendance history:', err);
      setError(err.message || 'Failed to load attendance history');
      setAttendances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (sessionId) => {
    navigate(`/attendance/tutor/view/${sessionId}?return=history`);
  };

  const handleWeekChange = (event) => {
    const value = event.target.value;
    setSelectedWeek(value);
    const week = teachingWeeks.find((item) => String(item.number) === value);
    if (week) {
      setFromDate(week.start_date);
      setToDate(week.end_date);
    }
  };

  const handleFromDateChange = (event) => {
    setSelectedWeek('');
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setSelectedWeek('');
    setToDate(event.target.value);
  };

  const handleTakeAttendance = (sessionId) => {
    navigate(`/attendance/tutor/mark/${sessionId}?return=history`);
  };

  const getStatusBadge = (state) => {
    const statusLower = state?.toLowerCase() || 'pending';
    
    switch (statusLower) {
      case 'marked':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800 flex items-center space-x-1">
            <MdCheckCircle />
            <span>Marked</span>
          </span>
        );
      case 'missed':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800 flex items-center space-x-1">
            <MdCancel />
            <span>Missed</span>
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
            Cancelled
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="border-b-2 border-gray-300 pb-4 sm:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-4 sm:mb-6 space-y-2 sm:space-y-0">
          <MdHistory className="text-2xl sm:text-3xl text-blue-500 flex-shrink-0" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Attendance History</h1>
        </div>

        {/* Date Filters */}
        <div className="bg-white border-2 border-gray-300 rounded-xl shadow-sm p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-end">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Teaching Week
              </label>
              <select
                value={selectedWeek}
                onChange={handleWeekChange}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
              >
                <option value="">All Weeks</option>
                {teachingWeeks.map((week) => (
                  <option key={week.number} value={week.number}>
                    {week.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={fetchHistory}
                disabled={loading}
                className="w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdSearch className="text-lg" />
                <span>{loading ? 'Searching...' : 'Search'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {recoveryWindow && recoveryWindow.status !== 'expired' && (
        <div className={`rounded-xl border p-4 sm:p-5 ${
          recoveryWindow.active
            ? 'border-amber-300 bg-amber-50'
            : 'border-blue-200 bg-blue-50'
        }`}>
          <div className="flex items-start gap-3">
            <MdSchedule className={`mt-0.5 flex-shrink-0 text-xl ${
              recoveryWindow.active ? 'text-amber-600' : 'text-blue-600'
            }`} />
            <div>
              <p className={`font-semibold ${
                recoveryWindow.active ? 'text-amber-900' : 'text-blue-900'
              }`}>
                {recoveryWindow.active
                  ? 'Missed-attendance recovery is open'
                  : 'Missed-attendance recovery opens September 28'}
              </p>
              <p className={`mt-1 text-sm ${
                recoveryWindow.active ? 'text-amber-800' : 'text-blue-800'
              }`}>
                {recoveryWindow.active
                  ? `You can submit your own missed sessions through ${formatDateReadable(recoveryWindow.end_date)}. Access closes automatically after that date.`
                  : `From ${formatDateReadable(recoveryWindow.start_date)} through ${formatDateReadable(recoveryWindow.end_date)}, you can submit your own missed sessions here.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading history...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 sm:p-6">
          <p className="text-red-700 font-medium text-sm sm:text-base">{error}</p>
        </div>
      )}

      {/* Attendance List */}
      {!loading && !error && (
        <>
          {attendances.length === 0 ? (
            <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-8 sm:p-12 text-center">
              <MdHistory className="text-4xl sm:text-5xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No Attendance Found</h3>
              <p className="text-sm sm:text-base text-gray-500">
                No attendance records found for the selected date range.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {attendances.map((session) => {
                const normalizedSession = normalizeSession(session);
                const batches = getBatchesArray(normalizedSession.batches);
                
                return (
                  <div
                    key={normalizedSession.id}
                    className="bg-white border-2 border-gray-300 rounded-xl p-4 sm:p-6 hover:shadow-lg transition"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 break-words">
                          {normalizedSession.subject}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <MdCalendarToday className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">
                              {normalizedSession.session_date ? formatDateReadable(normalizedSession.session_date) : 'N/A'}
                            </span>
                          </div>
                          {normalizedSession.time_start && normalizedSession.time_end && (
                            <div className="flex items-center space-x-2">
                              <MdSchedule className="text-gray-400 flex-shrink-0" />
                              <span>{normalizedSession.time_start} - {normalizedSession.time_end}</span>
                            </div>
                          )}
                          {batches.length > 0 && (
                            <div className="flex items-center space-x-2">
                              <MdPeople className="text-gray-400 flex-shrink-0" />
                              <span className="truncate">{batches.join(', ')}</span>
                            </div>
                          )}
                          {normalizedSession.period && (
                            <div>
                              <span className="font-medium">Period:</span> {normalizedSession.period}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:space-x-3">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {normalizedSession.teaching_week && (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                              {normalizedSession.teaching_week_label}
                            </span>
                          )}
                          {getStatusBadge(normalizedSession.state)}
                        </div>
                        {normalizedSession.attendance_summary && (
                          <div className="text-left sm:text-right text-xs sm:text-sm">
                            <p className="text-green-700 font-semibold">
                              {normalizedSession.attendance_summary.present || 0} Present
                            </p>
                            <p className="text-red-700">
                              {normalizedSession.attendance_summary.absent || 0} Absent
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {normalizedSession.can_mark ? (
                      <button
                        onClick={() => handleTakeAttendance(normalizedSession.id)}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-amber-500 text-amber-950 rounded-lg hover:bg-amber-400 active:scale-[0.98] transition-transform text-sm sm:text-base font-semibold shadow-sm"
                      >
                        {normalizedSession.state?.toLowerCase() === 'missed'
                          ? 'Take missed attendance'
                          : 'Take attendance'}
                      </button>
                    ) : normalizedSession.can_view && (
                      <button
                        onClick={() => handleViewDetails(normalizedSession.id)}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-transform text-sm sm:text-base font-semibold shadow-sm"
                      >
                        View Details
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {attendances.length > 0 && (
            <div className="mt-4 sm:mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 sm:p-6">
              <p className="text-xs sm:text-sm md:text-base text-blue-900 font-medium">
                Found <span className="font-bold">{attendances.length}</span> Attendance{attendances.length !== 1 ? 's' : ''} in the selected date range
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AttendanceHistory;

